/**
 * Centralized date formatting utilities
 */

export type DateInput = Date | string | undefined | null;

/**
 * Format date as "Jan 15, 2024" (short month)
 */
export const formatDate = (date: DateInput, fallback = '-'): string => {
  if (!date) return fallback;
  const d = new Date(date);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date as "January 15, 2024" (long month)
 */
export const formatDateLong = (date: DateInput, fallback = '-'): string => {
  if (!date) return fallback;
  const d = new Date(date);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format date with time as "Jan 15, 2024 2:30 PM"
 */
export const formatDateTime = (date: DateInput, fallback = '-'): string => {
  if (!date) return fallback;
  const d = new Date(date);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date for input fields (YYYY-MM-DD)
 */
export const formatDateForInput = (date: DateInput): string => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

/**
 * Parse UTC date string (adds 'Z' suffix if needed for proper UTC handling)
 */
export const parseUTCDate = (date: DateInput): Date | null => {
  if (!date) return null;
  const dateStr = typeof date === 'string' && !date.endsWith('Z') ? date + 'Z' : date;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};
